import './FilterChip.scss';
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { FontIcon, TextField, List, ListItemControl, Checkbox, Button } from 'react-md';
import _ from 'lodash';
import Fuse from 'fuse.js';
import { FUSE_OPTIONS, idFormatter } from '../../utils';

export class FilterChip extends Component {

  static propTypes = {
    direction: PropTypes.string,
    label: PropTypes.string.isRequired
  }

  static defaultProps = {
    direction: 'right'
  }

  constructor(props) {
    super(props);
    this._element = React.createRef();
  }

  state = {
    expanded: false,
    removable: false,
    selectAll: false,
    data: [],
    selected: [],
    searchTerm: ''
  }

  componentDidMount = () => {
    document.addEventListener('mousedown', this.handleClick, false);
    if (this.props.data) {
      this.setState({
        data: [...this.props.data]
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.data !== prevProps.data) {
      const newData = _.merge(this.props.data, this.state.data);
      this.setState({ data: newData });
      if (!this.props.activeFilter) {
        this.setState({ selected: [] });
      }
    }
  }

  componentWillUnmount = () => {
    document.removeEventListener('mousedown', this.handleClick, false);
  }

  handleClick = (e) => {
    if (this._element.current.contains(e.target)) {
      return;
    }
    const clearNonAppliedChecks = this.props.data.map(x => {
      if (this.state.selected.some(y => y === x.id)) {
        x.isChecked = true;
        return x;
      } else {
        x.isChecked = false;
        return x;
      }
    });

    this.setState({
      searchTerm: '',
      expanded: false,
      data: clearNonAppliedChecks,
      selected: clearNonAppliedChecks.filter(x => x.isChecked === true).map(x => x.id)
    });
  }

  countChecked = () => {
    return this.state.selected.length;
  }
  countNonAppliedChecked = () => {
    return _.filter(this.state.data, { isChecked: true }).length;
  }

  selectAll = () => this.setState({
    data: this.state.data.map(({ name, id }) => ({ name, id, isChecked: true }))
  });

  deselectAll = () => this.setState({
    data: this.props.data.map(({ name, id }) => ({ name, id, isChecked: false }))
  });

  isRemovable = () => {
    const { data } = this.state;
    return !!_.find(data, { isChecked: true });
  }

  handleSetExpand = (value, e) => {
    e.stopPropagation();
    this.setState({ expanded: value });
  }

  handleApply = (value, e) => {
    e.stopPropagation();
    this.setState({
      expanded: value
    });

    const selected = _.map(_.filter(this.state.data, { isChecked: true }), (item) => item.id);
    this.setState({ selected });
    this.props.onApply(selected);
  }

  handleRemove = (e) => {
    e.stopPropagation();
    this.deselectAll();
    this.setState({ searchTerm: '', selected: [] });
    this.props.onRemove(this.props.filterKey);
  }

  handleOnChangeCheckbox = (v, item) => {
    const { data } = this.state;
    const index = _.findIndex(data, o => o.id === item.id);
    data[index].isChecked = v;
    this.setState({ data });
  }

  handleOnChangeSelectAll = v => v ? this.selectAll() : this.deselectAll();

  isAllSelected = () => {
    const { data } = this.state;
    const containsCheckedFalse = _.some(data, ['isChecked', false]);

    return !containsCheckedFalse;
  }

  createFilteredList = () => {
    const { data, searchTerm } = this.state;
    const searchList = data;
    const fuse = new Fuse(searchList, FUSE_OPTIONS);
    const results = fuse.search(searchTerm);
    const filteredWithCheckState = results.map(result => _.find(data, ['id', result.id]));
    return searchTerm === '' ?
      data.sort((org1, org2) => {
        const name1 = org1.name ? org1.name.toUpperCase() : org1.name;
        const name2 = org2.name ? org2.name.toUpperCase() : org2.name;
        return (name1 < name2) ? -1 : (name1 > name2) ? 1 : 0;
      }) :
      filteredWithCheckState.sort((org1, org2) => {
        const name1 = org1.name ? org1.name.toUpperCase() : org1.name;
        const name2 = org2.name ? org2.name.toUpperCase() : org2.name;
        return (name1 < name2) ? -1 : (name1 > name2) ? 1 : 0;
      });
  }

  handleSearch = (searchTerm) => {
    this.setState({ searchTerm });
  }

  render() {
    const { label, direction } = this.props;
    const { expanded, searchTerm } = this.state;
    const filteredList = this.createFilteredList();

    return (
      <Fragment>
        {!expanded &&
          <div ref={this._element} id={idFormatter(label)} duration={200}>
            <div ref={this._element} className={`filter-chip ${this.isRemovable() ? 'removable' : ''}`} onClick={(e) => this.handleSetExpand(true, e)}>
              <div className="filter-chip-header">
                <span className="chip-label">
                  {`${label} ${this.isRemovable() ? '(' + this.countChecked() + ')' : ''}`}
                </span>
                <Fragment>
                  {this.isRemovable() &&
                    <button id={`${idFormatter(label)}-filter-cancel`} className="chip-remove-button" onClick={(e) => this.handleRemove(e)}>
                      <FontIcon>cancel</FontIcon>
                    </button>
                  }
                  {!expanded && !this.isRemovable() &&
                    <span id={`${idFormatter(label)}-filter-drop-down`} className="chip-icon">
                      <FontIcon>arrow_drop_down</FontIcon>
                    </span>
                  }
                </Fragment>
              </div>
            </div>
          </div>
        }
        {expanded &&
          <div ref={this._element} id={idFormatter(label)} duration={200}>
            <div className={`filter-chip expanded expand-${direction}`}>
              <div className="filter-chip-header">
                <span className="chip-label">
                  {label}
                </span>
                <button id={`${idFormatter(label)}-filter-close-chip`} className="chip-close-icon" onClick={(e) => this.handleSetExpand(false, e)}>
                  <FontIcon>close</FontIcon>
                </button>
              </div>
              <div className="filter-chip-dropdown">
                <h5 className="chip-section-header">Search</h5>
                <TextField
                  id={`${idFormatter(label)}-search-input`}
                  placeholder="What are you looking for...?"
                  rightIcon={<FontIcon>search</FontIcon>}
                  fullWidth
                  value={searchTerm}
                  onChange={(v) => this.handleSearch(v)}
                />
                <br />
                {!_.isEmpty(filteredList) &&
                  this.state.searchTerm === '' &&
                  <Checkbox
                    id={`${idFormatter(label)}-top-control-select`}
                    className="ph-checkbox"
                    label={`Select all (${this.props.data.length})`}
                    name="select-all"
                    labelBefore
                    onChange={(v) => this.handleOnChangeSelectAll(v)}
                    checked={this.isAllSelected()}
                  />
                }
                <List className="chip-list">
                  {filteredList && filteredList.map((item, index) => (
                    <ListItemControl
                      title={item.name}
                      key={index}
                      secondaryAction={(
                        <Checkbox
                          id={`${idFormatter(label)}- check - input - ${index}`}
                          className="ph-checkbox"
                          name={`${idFormatter(label)}-check-input-${index}`}
                          label={item.name}
                          onChange={(v) => this.handleOnChangeCheckbox(v, item)}
                          checked={item.isChecked}
                          labelBefore
                        />
                      )}
                    />
                  ))}
                </List>
                <br />
                <div className="row end-xs" id="apply-button">
                  <div className="col-xs">
                    <Button
                      disabled={this.countNonAppliedChecked() === 0}
                      id={`${idFormatter(label)}-filter-apply`}
                      className="filter-chip-apply-button"
                      flat
                      secondary
                      swapTheming
                      onClick={(e) => this.handleApply(false, e)}
                    >
                      Apply
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      </Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/components/FilterChip/FilterChip.jsx